/**
 * @file: useReservationActions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Every mutation the reservation-detail screen drives: the status transitions
 * (confirm / arrive / no-show / cancel), the editable arrival time + expected
 * items, and the create-order flow that opens the order modal pre-filled from
 * the reservation and, on creation, converts it (and its parent lead). Owns the
 * editor open/close state so the screen stays a thin composition root.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { ReservationsNavParams } from '@euler/app/flows/reservations';
import { useOpenReservationOrder } from '@euler/app/flows/reservations/functions/useOpenReservationOrder';
import { useReservationCenter } from '@euler/app/flows/reservations/provider';
import { useAppLoading } from '@euler/components/loading/Provider';
import { getCustomerTypes } from '@euler/functions/getCustomerTypes';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { OrgMember } from '@euler/model/entity';
import { OrderReservationType } from '@euler/model/enum/OrderReservationType';
import {
  Reservation,
  isTerminalReservationStatus,
} from '@euler/model/reservation/Reservation';
import {
  ReservationItem,
  parseReservationItems,
} from '@euler/model/reservation/ReservationItem';
import {
  ReservationTransitionAction,
  UpdateReservationRequest,
} from '@euler/model/reservation/types';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useState } from 'react';
import Toast from 'react-native-root-toast';

// Split a stored `tags` CSV into a clean list (trimmed, no blanks).
const splitTags = (tags: string | null | undefined): string[] =>
  tags
    ? tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
    : [];

export type TransitionItem = {
  label: string;
  action: ReservationTransitionAction;
};

/**
 * The one primary action that advances a reservation along its happy path:
 * pending → confirm, confirmed → arrive, arrived → create-order. Rendered as
 * the CTA below the info card. Null for terminal statuses (nothing to advance).
 */
export type PrimaryAction =
  | { kind: 'transition'; label: string; action: ReservationTransitionAction }
  | { kind: 'createOrder'; label: string };

export const primaryActionFor = (
  status: Reservation['status'],
): PrimaryAction | null => {
  switch (status) {
    case 'pending':
      return { kind: 'transition', label: '确认预约', action: 'confirm' };
    case 'confirmed':
      return { kind: 'transition', label: '标记到店', action: 'arrive' };
    case 'arrived':
      return { kind: 'createOrder', label: '开单' };
    default:
      return null;
  }
};

/**
 * The secondary transitions folded into the header ⋯ menu (not the CTA, and not
 * cancel — that gets its own destructive button at the bottom of the page).
 */
export const secondaryTransitionsFor = (
  status: Reservation['status'],
): TransitionItem[] => {
  switch (status) {
    case 'confirmed':
      return [{ label: '客户未到', action: 'no_show' }];
    default:
      return [];
  }
};

/** Whether the reservation can still be cancelled (any non-terminal status). */
export const canCancelReservation = (status: Reservation['status']): boolean =>
  !isTerminalReservationStatus(status);

/** Whether the reservation can be deleted (only once it has been cancelled). */
export const canDeleteReservation = (status: Reservation['status']): boolean =>
  status === 'cancelled';

export const useReservationActions = (
  id: string,
  reservation: Reservation | null | undefined,
  applyUpdate: (updated: Reservation) => void,
) => {
  const { reservationService } = useServiceFactory();
  const { reservationUpdated$, reservationCanceled$, reservationDeleted$ } =
    useReservationCenter();
  const navigation =
    useNavigation<StackNavigationProp<ReservationsNavParams>>();
  const appNavigation = useAppNavigation();
  const openReservationOrder = useOpenReservationOrder();
  const loading = useAppLoading();
  const confirm = useConfirm();
  const { showActionSheetWithOptions } = useActionSheet();

  const [editingArrival, setEditingArrival] = useState(false);

  // After a mutation: refetch the fresh row once, refresh this screen, and
  // broadcast it imperatively so sibling screens (the list) patch + recount.
  // Cancel publishes on its own dedicated channel.
  const broadcast = usePersistFn(async (canceled: boolean) => {
    const updated = await reservationService.get(id);
    applyUpdate(updated);
    (canceled ? reservationCanceled$ : reservationUpdated$).next(updated);
  });

  const runTransition = useCallback(
    async (item: TransitionItem) => {
      if (item.action === 'cancel' || item.action === 'no_show') {
        const ok = await confirm({
          title: item.action === 'cancel' ? '取消该预约?' : '标记客户未到?',
          destructive: true,
        });
        if (!ok) return;
      }
      await loading.showWithAction(async () => {
        await reservationService.transition(id, { action: item.action });
        await broadcast(item.action === 'cancel');
        Toast.show('已更新', { position: Toast.positions.BOTTOM });
      });
      // A cancelled booking is a dead-end here — pop back to the list (which
      // has already reacted to the broadcast).
      if (item.action === 'cancel') {
        navigation.goBack();
      }
    },
    [confirm, id, broadcast, loading, reservationService, navigation],
  );

  // The header ⋯ menu: the destructive/secondary transitions for this status.
  const openMoreMenu = useCallback(
    (items: TransitionItem[]) => {
      if (!items.length) return;
      const options = [...items.map(i => i.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: options.length - 1,
          destructiveButtonIndex: items.findIndex(i => i.action === 'cancel'),
        },
        selected => {
          if (selected == null || selected >= items.length) return;
          void runTransition(items[selected]);
        },
      );
    },
    [runTransition, showActionSheetWithOptions],
  );

  const onCreateOrder = useCallback(() => {
    if (reservation == null) return;
    const r = reservation;
    // The booked items seed the order's service subjects (assignees omitted —
    // inspection subjects are assigned in the order flow as usual).
    const serviceSubjects = parseReservationItems(r.items);
    // Carry the booked vehicle descriptor so the order's 品牌/车型 auto-fills
    // (plate / vin / mileage are passed separately below).
    const vehicleInfo =
      r.vehicleBrandName || r.vehicleModel || r.vehicleName
        ? {
            vehicleName: r.vehicleName,
            vehicleBrandName: r.vehicleBrandName,
            vehicleManufacturer: r.vehicleManufacturer ?? undefined,
            vehicleModel: r.vehicleModel,
            vehicleModelYear: r.vehicleModelYear ?? undefined,
            vehicleDisplacement: r.vehicleDisplacement ?? undefined,
            vehicleTrimLevel: r.vehicleTrimLevel ?? undefined,
            vehicleFuelType: r.vehicleFuelType ?? undefined,
          }
        : undefined;
    appNavigation.navigate('_order', {
      mode: 'new',
      type: 'main',
      defaultValues: {
        licensePlateNo: r.licensePlateNo,
        vin: r.vin ?? '',
        vehicleInfo,
        mileage: r.vehicleMileage ?? r.currentMileage ?? 0,
        customerName: r.customerName ?? undefined,
        customerMobile: r.customerPhone ?? undefined,
        customerType: r.customerType ?? undefined,
        tags: r.tags ? r.tags.split(',').filter(Boolean) : undefined,
        serviceAgentInfo: r.serviceAgentId
          ? { serviceAgentId: r.serviceAgentId, name: r.serviceAgentName }
          : undefined,
        remark: r.remark ?? undefined,
        serviceSubjects: serviceSubjects.length ? serviceSubjects : undefined,
        reservationType: OrderReservationType.Reserved,
        // The backend converts this reservation + its parent lead once the
        // order is created — no client-side convert call needed.
        reservationId: r.id,
      },
      onCreated: () => {
        // The reservation + lead were converted server-side during order
        // creation; refresh this screen + the list to reflect it.
        void broadcast(false);
      },
    });
  }, [appNavigation, broadcast, reservation]);

  // Open the converted order (task detail while in-progress, service record once
  // finished — see useOpenReservationOrder).
  const openOrder = useCallback(
    () => void openReservationOrder(reservation?.orderNo),
    [openReservationOrder, reservation?.orderNo],
  );

  // Persist a partial edit, refresh this screen, and broadcast the fresh row so
  // the list patches in place. Every inline field editor funnels through here.
  const patch = useCallback(
    async (req: UpdateReservationRequest) => {
      await loading.showWithAction(async () => {
        await reservationService.update(id, req);
        await broadcast(false);
        Toast.show('已更新', { position: Toast.positions.BOTTOM });
      });
    },
    [id, broadcast, loading, reservationService],
  );

  const saveArrival = useCallback(
    async (date: Date) => {
      setEditingArrival(false);
      await patch({ expectedArrivalAt: date.toISOString() });
    },
    [patch],
  );

  // Add/remove both write the whole array (items live in one JSON column).
  const replaceItems = useCallback(
    (items: ReservationItem[]) => patch({ items }),
    [patch],
  );

  // Advisor: pick from the same selector the create flow uses; id + name move
  // together.
  const editAgent = useCallback(() => {
    navigation.push('_serviceAgentSelector', {
      selectedServiceAgentId: reservation?.serviceAgentId || undefined,
      onSelect: (member: OrgMember) =>
        void patch({
          serviceAgentId: member.id,
          serviceAgentName: member.name,
        }),
    });
  }, [navigation, reservation?.serviceAgentId, patch]);

  // Business-type tags edit in place against the stored CSV (one round trip per
  // add/remove, like the items panel).
  const addTag = useCallback(
    (tag: string) => {
      const current = splitTags(reservation?.tags);
      if (current.includes(tag)) return;
      void patch({ tags: [...current, tag].join(',') });
    },
    [reservation?.tags, patch],
  );
  const removeTag = useCallback(
    (tag: string) => {
      const current = splitTags(reservation?.tags);
      void patch({ tags: current.filter(t => t !== tag).join(',') });
    },
    [reservation?.tags, patch],
  );

  // Customer type: an action sheet over the known types, plus a clear option.
  const editCustomerType = useCallback(() => {
    const types = getCustomerTypes();
    const options = [...types.map(t => t.name), '清除', '取消'];
    showActionSheetWithOptions(
      { title: '客户类型', options, cancelButtonIndex: options.length - 1 },
      selected => {
        if (selected == null) return;
        if (selected < types.length) {
          void patch({ customerType: types[selected].type });
        } else if (selected === types.length) {
          void patch({ customerType: null });
        }
      },
    );
  }, [showActionSheetWithOptions, patch]);

  // Estimated mileage: committed inline from its row (digits only); empty
  // clears it.
  const saveMileage = useCallback(
    async (value: string) => {
      const digits = value.replace(/[^0-9]/g, '');
      await patch({ vehicleMileage: digits ? Number(digits) : null });
    },
    [patch],
  );

  // Tapping an item opens its action sheet; removing one confirms first.
  const openItemActions = useCallback(
    (index: number) => {
      const current = parseReservationItems(reservation?.items);
      if (index < 0 || index >= current.length) return;
      showActionSheetWithOptions(
        {
          options: ['移除项目', '取消'],
          cancelButtonIndex: 1,
          destructiveButtonIndex: 0,
          title: current[index].name,
        },
        async selected => {
          if (selected !== 0) return;
          const ok = await confirm({ title: '移除该项目?', destructive: true });
          if (!ok) return;
          void replaceItems(current.filter((_, i) => i !== index));
        },
      );
    },
    [reservation?.items, showActionSheetWithOptions, confirm, replaceItems],
  );

  // The CTA below the info card: advance the status, or open the order.
  const runPrimary = useCallback(
    (primary: PrimaryAction) => {
      if (primary.kind === 'createOrder') {
        onCreateOrder();
      } else {
        void runTransition({ label: primary.label, action: primary.action });
      }
    },
    [onCreateOrder, runTransition],
  );

  // The destructive cancel at the bottom of the page (confirms first).
  const cancel = useCallback(
    () => void runTransition({ label: '取消预约', action: 'cancel' }),
    [runTransition],
  );

  // Delete a cancelled reservation (soft delete); broadcasts the id so the list
  // drops the card, then pops back.
  const remove = useCallback(async () => {
    const ok = await confirm({
      title: '删除该预约?',
      message: '删除后将不再显示',
      destructive: true,
    });
    if (!ok) return;
    await loading.showWithAction(async () => {
      await reservationService.delete(id);
      reservationDeleted$.next(id);
      Toast.show('已删除', { position: Toast.positions.BOTTOM });
    });
    navigation.goBack();
  }, [
    confirm,
    id,
    loading,
    navigation,
    reservationDeleted$,
    reservationService,
  ]);

  // Stable open/close so the memoized header panel can skip renders.
  const openArrival = useCallback(() => setEditingArrival(true), []);
  const closeArrival = useCallback(() => setEditingArrival(false), []);

  return {
    runPrimary,
    openMoreMenu,
    cancel,
    remove,
    onCreateOrder,
    openOrder,
    replaceItems,
    openItemActions,
    editAgent,
    addTag,
    removeTag,
    editCustomerType,
    saveMileage,
    arrival: {
      editing: editingArrival,
      open: openArrival,
      close: closeArrival,
      save: saveArrival,
    },
  };
};
